import { shareImage } from "@/lib/social-sharing";
import { ShareRow } from "@/components/site/ShareRow";
import portrait from "@/assets/arda-event-face.webp.asset.json";
import { listApprovedThemes } from "@/lib/themes-public.functions";
import { themeByKey, type ApprovedTheme } from "@/lib/themes-public";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { BookCard, IdeasBetween, PostRows, SectionTitle, ThemeKicker, pickIdeasBetween, type ThemedPost } from "@/components/site/blocks";
import { THEME_BOOK, BOOKS } from "@/components/site/theme-ui";
import { DemoNewsletter } from "@/components/tr-home/TrHomeRedesign";
import { localizeOwnSiteLinks, repairLegacyImageLinks } from "@/lib/own-site-links";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { autoLinkGlossary } from "@/lib/glossary-autolink";

import { getPostBySlug, getAllPosts } from "@/lib/posts.functions";
import { sourcesMode, type SourcesMode } from "@/lib/source-preview";
import { ArticleSources, sourceStructuredData } from "@/components/site/ArticleSources";
import { getMagazineBySlug } from "@/lib/magazine.functions";
import { getArticleMeta } from "@/lib/articles-meta";
import { useLocalizedPost } from "@/lib/wp.hooks";
import { useT } from "@/lib/i18n";
import { ArticleEnhancements } from "@/components/ArticleEnhancements";
import { TagChips } from "@/components/TagChips";
import { getTag } from "@/lib/tags";
import { verifiedTrArticleRedirect, isGoneLegacyPath } from "@/lib/redirects";
import { MissingPage } from "@/components/MissingPage";


import { ReadingProgressBar } from "@/components/ReadingProgressBar";
import { resizeImg, storageSrcSet, storageVariant } from "@/lib/img";
import {
  hreflangLinks,
  enCounterpartForTrPath,
  trPersonSchema,
  trPublisherSchema,
  TR_PERSON_ID,
  TR_PUBLISHER_ID,
} from "@/lib/seo";
import { computeEssayGraph } from "@/lib/knowledge-graph";
import { listPublishedReports } from "@/lib/reports.functions";


import { extractFaqPairs, plainTextLength } from "@/lib/faq-extract";
import { pageTitle } from "@/lib/brand";
import type { WpArticle, WpArticleFull } from "@/lib/posts.types";


/**
 * Per-slug SEO overrides. Lets a specific post replace the auto-generated
 * title/description/og:image and inject an FAQPage JSON-LD without touching
 * the shared route logic for every other essay.
 */
type SlugOverride = {
  title?: string;
  description?: string;
  ogImageUrl?: string;
  faq?: { question: string; answer: string }[];
};

const SLUG_OVERRIDES: Record<string, SlugOverride> = {
  "yapay-cazibe-ile-cozulen-muhakeme": {
    title: "Yapay Cazibe ile Çözülen Muhakeme | Yapay Zeka Etiği",
    description:
      "Yapay zeka doğruya benzeyen cevaplarla muhakememizi nasıl zayıflatıyor? Arda Öztaşkın, tereddüt refleksinin önemini ve kurumlar için riski anlatıyor. 6 dk okuma.",
    ogImageUrl: "https://akillisurdurulebilirlik.com/og/yapay-cazibe-ile-cozulen-muhakeme.png",
    faq: [
      {
        question: "Yapay zekanın en büyük riski nedir?",
        answer:
          "Yanlış cevap üretmesi değil, doğruya yeterince benzeyen ikna edici cevaplarla tereddüt refleksimizi örselemesidir. İnsan zihni hatalarla değil, makul görünen doğrularla daha kolay aldatılır; bu da muhakemeyi zamanla zayıflatır.",
      },
    ],
  },
};

type EssayLoaderData = {
  post: WpArticleFull;
  allPosts: WpArticle[];
  reports: Awaited<ReturnType<typeof listPublishedReports>>;
  faq: ReturnType<typeof extractFaqPairs>;
  themes: ApprovedTheme[];
  sourcesMode: SourcesMode;
};

async function loadEssay({ params }: { params: { slug: string } }): Promise<EssayLoaderData> {
  const post = await getPostBySlug({ data: { slug: params.slug } });
  if (!post) {
    const staticArticle = getArticleMeta(params.slug);
    if (staticArticle) {
      throw redirect({ to: "/dergi/$slug", params: { slug: params.slug }, statusCode: 301 });
    }
    const mag = await getMagazineBySlug({ data: { slug: params.slug } }).catch(() => null);
    if (mag) {
      throw redirect({ to: "/dergi/$slug", params: { slug: params.slug }, statusCode: 301 });
    }
    throw notFound();
  }
  const [allPosts, reports, themes] = await Promise.all([
    getAllPosts().catch(() => []),
    listPublishedReports().catch(() => []),
    listApprovedThemes().catch(() => []),
  ]);
  const html = post.content ?? "";
  const faq = extractFaqPairs(html);
  const mode = sourcesMode();
  return { post, allPosts, reports, faq, themes, sourcesMode: mode };
}

export const Route = createFileRoute("/yazilar/$slug")({
  beforeLoad: ({ location }) => {
    if (isGoneLegacyPath(location.pathname)) throw notFound();
    const target = verifiedTrArticleRedirect(location.pathname);
    if (target) {
      throw redirect({ href: `${target}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`, statusCode: 301 });
    }
  },
  loader: loadEssay,
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: pageTitle("Essay", "tr") }] };
    const override = SLUG_OVERRIDES[params.slug] ?? {};
    // Keep <title> under ~60 chars: append the site suffix only when it fits,
    // otherwise use the bare post title (truncated at a word boundary).
    const rawTitle = override.title ?? loaderData.post.seoTitle ?? loaderData.post.title;
    const withSuffix = `${rawTitle} — Akıllı Sürdürülebilirlik`;
    const title = override.title
      ? override.title
      : withSuffix.length <= 60
        ? withSuffix
        : rawTitle.length <= 60
          ? rawTitle
          : rawTitle.slice(0, 57).replace(/[\s,;:–—-]+$/, "") + "…";
    const description = override.description ?? loaderData.post.excerpt;
    const ogTitle = override.title ?? loaderData.post.seoTitle ?? loaderData.post.title;
    const ogImgSrc = shareImage(loaderData.post.img || override.ogImageUrl);
    const overrideFaq = override.faq ?? [];
    const plainText = (loaderData.post.content ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const wordCount = plainText ? plainText.split(" ").filter(Boolean).length : undefined;
    const stop = new Set(["ve","ile","bir","bu","da","de","için","ama","gibi","the","and","of","to","a","in","is","that","için","olarak","ise","daha","çok","en"]);
    const titleKeywords = loaderData.post.title
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .split(/\s+/)
      .filter((w) => w.length > 3 && !stop.has(w));
    // Tag names (closed vocabulary) are the strongest topical signal we have —
    // they lead the keyword list and are emitted as article:tag meta.
    const tagNames = (loaderData.post.tags ?? [])
      .map((s) => getTag(s)?.tr)
      .filter((n): n is string => !!n);
    // Section and theme keywords come only from the editor-approved theme
    // relation; the legacy `category` column is never emitted.
    const approvedTheme = (loaderData.themes ?? []).find((t) => t.slug === loaderData.post.slug);
    const themeSection = themeByKey(approvedTheme?.primary)?.title;
    const themeSecondary = themeByKey(approvedTheme?.secondary)?.title;
    const keywords = Array.from(
      new Set([themeSection, themeSecondary, ...tagNames, ...titleKeywords].filter(Boolean) as string[]),
    ).slice(0, 12);


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: ogTitle },
        { property: "og:description", content: description },
        { name: "twitter:title", content: ogTitle },
        { name: "twitter:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://akillisurdurulebilirlik.com/yazilar/${params.slug}` },
        { property: "article:author", content: "Arda Öztaşkın" },
        ...(loaderData.post.isoDate
          ? [{ property: "article:published_time", content: loaderData.post.isoDate }]
          : []),
        ...(themeSection
          ? [{ property: "article:section", content: themeSection }]
          : []),
        // Note: the head manager dedupes by property, so a single joined
        // article:tag carries the whole set instead of being collapsed to one.
        ...(tagNames.length ? [{ property: "article:tag", content: tagNames.join(", ") }] : []),

        ...(keywords.length ? [{ name: "keywords", content: keywords.join(", ") }] : []),

        { property: "og:locale", content: "tr_TR" },
        // Only declare an English alternate when the article really has one;
        // this mirrors the rel="alternate" hreflang links below.
        ...(enCounterpartForTrPath(`/yazilar/${params.slug}`)
          ? [{ property: "og:locale:alternate", content: "en_US" }]
          : []),

        ...(ogImgSrc
          ? [
              { property: "og:image", content: ogImgSrc },
              { property: "og:image:alt", content: ogTitle },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: ogImgSrc },
            ]
          : []),
      ],
      links: [
        { rel: "canonical", href: `https://akillisurdurulebilirlik.com/yazilar/${params.slug}` },
        ...hreflangLinks(`/yazilar/${params.slug}`, enCounterpartForTrPath(`/yazilar/${params.slug}`)),
        // No manual hero preload: React emits one from the fetchPriority="high"
        // <img> below with the same srcset/sizes, so the browser fetches one
        // right-sized file instead of a mismatched preload + image.
      ],
      scripts: [
        // Author and publisher as standalone nodes: the Article below points at
        // them by @id so Google Haberler/Discover can resolve the byline.
        { type: "application/ld+json", children: JSON.stringify(trPersonSchema()) },
        { type: "application/ld+json", children: JSON.stringify(trPublisherSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.post.title,
            description,
            ...(keywords.length ? { keywords: keywords.join(", ") } : {}),
            ...(wordCount ? { wordCount } : {}),
            image: ogImgSrc ? [ogImgSrc] : undefined,
            datePublished: loaderData.post.isoDate,
            dateModified: loaderData.post.isoDate,
            author: { "@id": TR_PERSON_ID },
            publisher: { "@id": TR_PUBLISHER_ID },
            mainEntityOfPage: `https://akillisurdurulebilirlik.com/yazilar/${params.slug}`,
            url: `https://akillisurdurulebilirlik.com/yazilar/${params.slug}`,
            ...(themeSection ? { articleSection: themeSection } : {}),
            inLanguage: "tr-TR",
            // Same sources the page shows under "Kaynakça ve Atıf".
            ...(loaderData.sourcesMode === "hidden"
              ? {}
              : loaderData.sourcesMode === "approved"
              ? sourceStructuredData(loaderData.post.approvedSources ?? [])
              : loaderData.post.externalSources?.length
                ? {
                    citation: loaderData.post.externalSources
                      .filter((s) => s?.url?.startsWith("http"))
                      .map((s) => ({ "@type": "CreativeWork", name: s.title || s.host, url: s.url })),
                  }
                : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
              { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
              {
                "@type": "ListItem",
                position: 3,
                name: loaderData.post.title,
                item: `https://akillisurdurulebilirlik.com/yazilar/${params.slug}`,
              },
            ],
          }),
        },
        ...(loaderData.faq && loaderData.faq.length >= 2
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: loaderData.faq.slice(0, 5).map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                  })),
                }),
              },
            ]
          : overrideFaq.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: overrideFaq.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: EssayPage,
  notFoundComponent: NotFound,
  errorComponent: EssayError as never,
});

function NotFound() {
  const { t, lang } = useT();
  const { slug } = Route.useParams();
  if (isGoneLegacyPath(`/yazilar/${slug}`)) return <MissingPage gone />;
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl mb-4">{t("essay.not_found")}</h1>
      <Link to={lang === "tr" ? "/yazilar" : "/ideas"} className="text-clay underline">
        {t("essay.browse")}
      </Link>
    </div>
  );
}

function EssayError({ error }: { error: Error }) {
  const { t } = useT();
  if (typeof console !== "undefined") console.error(error);
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-3xl mb-4">{t("essay.load_err")}</h1>
      <p className="text-ink/70 mb-6">{t("err.body")}</p>
      <Link to="/" className="text-clay underline">
        {t("essay.home")}
      </Link>
    </div>
  );
}

function EssayPage() {
  const { post: initial, allPosts, reports, themes, sourcesMode: mode } = Route.useLoaderData() as EssayLoaderData;
  const { post } = useLocalizedPost(initial);
  const { lang } = useT();
  const enhancementLang: "tr" | "en" = lang === "tr" ? "tr" : "en";
  const sources = mode === "legacy" ? (post.externalSources ?? []) : [];
  const approved = mode === "approved" ? (initial.approvedSources ?? []) : [];
  const themeOf = new Map(themes.map((t) => [t.slug, t]));
  const primary = themeOf.get(post.slug)?.primary ?? null;
  const themePage = themeByKey(primary);
  const graph = computeEssayGraph(post, enhancementLang, allPosts, reports);
  const chosen = (post.relatedSlugs ?? []).map((s) => allPosts.find((p) => p.slug === s)).filter((p): p is NonNullable<typeof p> => !!p);
  const trail: ThemedPost[] = (chosen.length ? chosen : graph.essays).slice(0, 3).map((p) => ({ ...p, theme: themeOf.get(p.slug)?.primary ?? null }));
  const between = primary
    ? pickIdeasBetween(allPosts, themeOf, primary, new Set([post.slug, ...trail.map((p) => p.slug)]))
    : [];
  const book = post.bookId && post.bookId in BOOKS ? BOOKS[post.bookId as keyof typeof BOOKS] : primary ? THEME_BOOK[primary] : null;
  const shareUrl = `https://akillisurdurulebilirlik.com/yazilar/${post.slug}`;
  return (
    <>
      <ReadingProgressBar />
      <div className="mx-auto max-w-7xl px-5 pt-5 sm:px-8 lg:pt-7">
        <Breadcrumbs
          items={[
            themePage ? { label: themePage.title, to: "/tema/$slug", params: { slug: themePage.slug } } : { label: "Yazılar", to: "/yazilar" },
            { label: post.title },
          ]}
        />
      </div>
      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-10 pt-6 sm:px-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-12 lg:pt-10 xl:grid-cols-[minmax(0,1fr)_320px]">
        <article className="min-w-0">
          <ThemeKicker theme={primary} date={post.date} className="text-[11px]" />
          <h1 className="mt-3 max-w-3xl text-balance font-serif text-[38px] font-medium leading-[1] sm:text-[52px] lg:text-[60px]">{post.title}</h1>
          {post.excerpt && (
            <p className="mt-4 max-w-2xl font-serif text-[19px] leading-snug text-ink/75 sm:text-[22px]">{post.excerpt}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/yazar" className="inline-flex items-center gap-2.5 text-sm text-ink hover:text-clay">
              <img src={portrait.url} alt="" width={32} height={32} className="size-8 rounded-full object-cover grayscale" />
              Arda Öztaşkın
            </Link>
            <span aria-hidden className="h-5 w-px bg-ink/15" />
            <ShareRow
              url={shareUrl}
              title={post.title}
              img={post.img ? resizeImg(post.img, 1080) : undefined}
              excerpt={post.excerpt ?? undefined}
              category={themePage?.title}
              lang={enhancementLang}
            />
            <span className="text-xs text-ink/60">{post.readTime}</span>
          </div>
          {post.img && (
            <img
              src={storageVariant(post.img, 1200, 16 / 9)}
              srcSet={storageSrcSet(post.img, 16 / 9)}
              sizes="(min-width: 1280px) 860px, (min-width: 1024px) calc(100vw - 420px), calc(100vw - 40px)"
              alt={post.title}
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
              className="mt-6 aspect-[16/9] w-full object-cover lg:aspect-[2/1]"
            />
          )}
          <div
            className="article-body essay-prose prose max-w-none font-serif text-ink/90 prose-headings:font-serif prose-a:text-theme-tech mt-8"
            dangerouslySetInnerHTML={{
              __html: repairLegacyImageLinks(localizeOwnSiteLinks(autoLinkGlossary(sanitizeHtml(post.content), lang))),
            }}
          />
          <ArticleEnhancements
            lang={enhancementLang}
            summary={[]}
            takeaways={[]}
            learning={[]}
            concepts={[]}
            sources={sources}
            citeOnly={mode !== "legacy"}
            faq={[]}
            citation={{ title: post.title, slug: post.slug, isoDate: post.isoDate }}
          />
          {approved.length > 0 && <ArticleSources sources={approved} />}
          <TagChips tags={post.tags} lang="tr" className="mt-10" />
        </article>

        <aside className="min-w-0 space-y-8 lg:border-l lg:border-ink/10 lg:pl-8">
          {trail.length > 0 && (
            <section aria-labelledby="yazinin-izinde">
              <SectionTitle id="yazinin-izinde" title="Bu yazının izinde" />
              <div className="mt-2"><PostRows posts={trail} showTheme size="sm" /></div>
            </section>
          )}
          {book && <BookCard book={book} />}
        </aside>
      </div>
      <IdeasBetween posts={between} intro="Bu yazı, diğer düşünce alanlarıyla da yakından ilişkili." />
      <DemoNewsletter />
    </>
  );
}
